/*
  Just Tattoos — collection + search behaviour.
  Plain ES2020, no dependencies, loaded with defer. Progressive: without this
  file the filter form is a plain GET form (with its noscript Apply button),
  the sort select submits natively, and "Load more" is a normal link to
  ?page=N. With this file, filters/sort/load-more refresh the grid through the
  Section Rendering API and update history without a full reload.
*/
(function () {
  'use strict';

  var doc = document;
  var body = doc.body;

  function q(selector, root) { return (root || doc).querySelector(selector); }
  function qa(selector, root) {
    return Array.prototype.slice.call((root || doc).querySelectorAll(selector));
  }

  function sectionEl() { return q('[data-collection-section]'); }
  function sectionId() {
    var el = sectionEl();
    return el ? el.getAttribute('data-section-id') : null;
  }

  /* Filter drawer ------------------------------------------------------- */
  var lastFocused = null;

  function drawerEl() { return q('[data-filter-drawer]'); }
  function isDrawerOpen() {
    var el = drawerEl();
    return !!(el && el.getAttribute('data-open') === 'true');
  }

  function setDrawerOpen(open, focus) {
    var el = drawerEl();
    if (!el) return;
    if (open) {
      lastFocused = doc.activeElement;
      el.setAttribute('data-open', 'true');
      el.setAttribute('aria-hidden', 'false');
      body.classList.add('is-filter-open');
      var trigger = q('[data-filter-open]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (focus) {
        var target = q('[data-filter-close].icon-btn', el) || q('button, input, select, a[href]', el);
        if (target) target.focus();
      }
    } else {
      el.setAttribute('data-open', 'false');
      el.setAttribute('aria-hidden', 'true');
      body.classList.remove('is-filter-open');
      var btn = q('[data-filter-open]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (focus && lastFocused && lastFocused.focus) lastFocused.focus();
    }
  }

  function openDrawer() { setDrawerOpen(true, true); }
  function closeDrawer() { setDrawerOpen(false, true); }

  /* Section Rendering API ------------------------------------------------ */
  var inflight = 0;

  function fetchSection(target) {
    var url = new URL(target, window.location.origin);
    var id = sectionId();
    if (id) url.searchParams.set('section_id', id);
    return fetch(url.toString(), { headers: { Accept: 'text/html' } }).then(function (response) {
      if (!response.ok) throw new Error('Section request failed');
      return response.text();
    });
  }

  function swapContent(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-collection-content]');
    var current = q('[data-collection-content]');
    if (fresh && current) current.replaceWith(doc.importNode(fresh, true));
  }

  function formToUrl(form) {
    var action = form.getAttribute('action') || window.location.href;
    var url = new URL(action, window.location.origin);
    url.search = '';
    var data = new FormData(form);
    data.forEach(function (value, key) {
      if (value !== '' && value != null) url.searchParams.append(key, value);
    });
    return url;
  }

  function navigate(href, options) {
    options = options || {};
    var url = new URL(href, window.location.origin);
    url.searchParams.delete('section_id');
    var target = url.toString();
    var wasOpen = isDrawerOpen();
    var ticket = ++inflight;
    var content = q('[data-collection-content]');
    if (content) content.setAttribute('aria-busy', 'true');

    fetchSection(target)
      .then(function (html) {
        if (ticket !== inflight) return; // a newer request superseded this one
        swapContent(html);
        if (wasOpen) setDrawerOpen(true, false);
        applyMarkdownLinks(q('[data-collection-content]'));
        if (options.push !== false) history.pushState({}, '', target);
      })
      .catch(function () {
        if (ticket === inflight) window.location.href = target;
      });
  }

  function applyFilters(form) {
    navigate(formToUrl(form).toString(), { push: true });
  }

  /* Load more ------------------------------------------------------------ */
  function setBusy(el, busy) {
    if (!el) return;
    el.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function loadMore(link) {
    var href = link.getAttribute('href');
    if (!href || link.getAttribute('aria-busy') === 'true') return;
    setBusy(link, true);
    var url = new URL(href, window.location.origin);
    var id = sectionId();
    if (id) url.searchParams.set('section_id', id);

    fetch(url.toString(), { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Load more failed');
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var grid = q('[data-collection-grid]');
        var freshGrid = parsed.querySelector('[data-collection-grid]');
        var firstNew = null;
        if (grid && freshGrid) {
          Array.prototype.slice.call(freshGrid.children).forEach(function (node) {
            var imported = doc.importNode(node, true);
            if (!firstNew) firstNew = imported;
            grid.appendChild(imported);
          });
        }
        var freshCount = parsed.querySelector('[data-collection-count]');
        var count = q('[data-collection-count]');
        if (freshCount && count) count.innerHTML = freshCount.innerHTML;

        var freshMore = parsed.querySelector('[data-loadmore]');
        var currentMore = q('[data-loadmore]');
        if (currentMore) {
          if (freshMore) currentMore.replaceWith(doc.importNode(freshMore, true));
          else currentMore.remove();
        }
        var focusTarget = firstNew && firstNew.querySelector('a[href]');
        if (focusTarget) focusTarget.focus({ preventScroll: true });

        var pageParam = new URL(href, window.location.origin).searchParams.get('page');
        if (pageParam) {
          var next = new URL(window.location.href);
          next.searchParams.set('page', pageParam);
          history.replaceState({}, '', next.toString());
        }
      })
      .catch(function () {
        window.location.href = href;
      })
      .finally(function () {
        setBusy(link, false);
      });
  }

  /* Markdown links [label](url) -> <a> ------------------------------------ */
  function escapeHtml(text) {
    return text.replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function markdownToHtml(text) {
    var out = '';
    var last = 0;
    var re = /\[([^\]]+)\]\(([^)\s]+)\)/g;
    var match;
    while ((match = re.exec(text))) {
      out += escapeHtml(text.slice(last, match.index));
      var url = match[2];
      if (!/^(https?:\/\/|\/|#|mailto:)/i.test(url)) { out += escapeHtml(match[0]); last = re.lastIndex; continue; }
      out += '<a href="' + escapeHtml(url) + '">' + escapeHtml(match[1]) + '</a>';
      last = re.lastIndex;
    }
    return out + escapeHtml(text.slice(last));
  }

  function applyMarkdownLinks(scope) {
    qa('[data-markdown-link]', scope || doc).forEach(function (el) {
      var raw = el.getAttribute('data-md-raw');
      if (raw == null) {
        raw = el.textContent;
        el.setAttribute('data-md-raw', raw);
      }
      if (raw.indexOf('](') !== -1) el.innerHTML = markdownToHtml(raw);
    });
  }

  /* Delegated events ----------------------------------------------------- */
  doc.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    if (target.closest('[data-filter-open]')) { event.preventDefault(); openDrawer(); return; }
    if (target.closest('[data-filter-close]')) { event.preventDefault(); closeDrawer(); return; }

    var loadMoreLink = target.closest('[data-load-more]');
    if (loadMoreLink) { event.preventDefault(); loadMore(loadMoreLink); return; }

    var chip = target.closest('.collection-active-filters__chip, .collection-active-filters__clear, .filter-drawer__clear');
    if (chip && chip.getAttribute('href') && sectionEl()) {
      event.preventDefault();
      navigate(chip.getAttribute('href'), { push: true });
    }
  });

  doc.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.matches) return;

    if (target.matches('[data-search-sort]')) {
      if (target.form) target.form.submit();
      return;
    }

    if (target.matches('input, select') && target.form && target.form.matches('[data-collection-form]')) {
      if (target.matches('input[type="number"]')) return; // price fields apply on submit
      applyFilters(target.form);
    }
  });

  doc.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches) return;
    if (form.matches('[data-collection-form]')) {
      event.preventDefault();
      applyFilters(form);
    }
  });

  doc.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isDrawerOpen()) { closeDrawer(); return; }
    if (event.key !== 'Tab' || !isDrawerOpen()) return;
    var el = drawerEl();
    if (!el) return;
    var nodes = qa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', el);
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  window.addEventListener('popstate', function () {
    if (sectionEl()) navigate(window.location.href, { push: false });
  });

  applyMarkdownLinks(doc);
})();
